import { store } from '../store';

export const getLtiToken = () => store.getState().user.user?.accessToken;

/**
 * Choose the API authentication mode from this tab's route, never from Redux.
 *
 * Redux persistence uses origin-wide localStorage. Route selection prevents an
 * LTI launch in one tab from switching a standalone portal tab to LTI APIs.
 */
export const isLtiRoute = (pathname = window.location.pathname) =>
  pathname === '/lti' || pathname.startsWith('/lti/');

export const getImpersonatedUserId = () => {
  const { impersonation, isImpersonating } = store.getState().impersonation ?? {};

  if (!isImpersonating) return null;

  const impersonatedUserId = impersonation?.midas;
  if (typeof impersonatedUserId !== 'string' || !impersonatedUserId.trim()) {
    /*
     * Never omit the header while the UI says impersonation is active. The
     * backend owns MIDAS format validation and rejects malformed values.
     */
    throw new Error(
      'The saved impersonation identity is missing. Revert to self and select the user again.',
    );
  }

  return impersonatedUserId;
};

/**
 * What the browser can say about itself, sent along with feedback so that a bug
 * someone reports can be looked for on the thing they saw it on.
 *
 * `userAgentData` is asked first where it exists, since the plain user agent
 * string is being frozen by browsers; the string is the fallback.
 * @returns {Promise<Object>} A promise that resolves to the browser's own account of itself
 */
export const getBrowserMetadata = async () => {
  const uaData = navigator.userAgentData;

  return {
    userAgent: uaData
      ? await uaData.getHighEntropyValues([
          'platform',
          'platformVersion',
          'architecture',
          'model',
          'uaFullVersion',
        ])
      : navigator.userAgent,
    language: navigator.language,
    cookiesEnabled: navigator.cookieEnabled,
    online: navigator.onLine,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
    pageUrl: window.location.href,
  };
};
